import React, { useMemo, useCallback } from "react";
import { TextField, TextFieldProps } from "@material-ui/core";
import { AccountContact, ContactType } from "../../../api/services";
import { useFormContext } from "react-hook-form";

export type PhoneNumberProp = {
  id: "1" | "2" | "3";
  contact: AccountContact;
  requireSms: boolean;
  contactType?: ContactType;
  fieldProps?: Partial<TextFieldProps>;
};

type ValidationRule = {
  regex: RegExp;
  helperText: string;
  errorMessage: string;
};
export const REGEXES = new Map<any, ValidationRule>();
// Allows extensions
// eslint-disable-next-line no-useless-escape
REGEXES.set(`(0|\+[0-9]{1,3})(\d{9,11})(\s?((ext|x)\.?\s?)(\d{1,6}?))?`, {
  regex: /^(0|\+[0-9]{1,3})(\d{9,11})(\s?((ext|x)\.?\s?)(\d{1,6}?))?$/i,
  helperText: "",
  errorMessage:
    "Please enter a phone number starting with 0 or +44 followed by min 9 digits",
});
// Allows numbers and special character
// eslint-disable-next-line no-useless-escape
REGEXES.set(`(0|\+[0-9]{1,3})(\d{9,11})`, {
  regex: /^(0|\+[0-9]{1,3})(\d{9,11})$/i,
  helperText: "",
  errorMessage:
    "Please enter a mobile number starting with 0 or +44 followed by min 9 digits",
});
REGEXES.set("fallback", {
  regex: /^([(). +-]|\d|(ext|x))+$/i,
  helperText: "",
  errorMessage: "Please enter a valid number",
});

export const PhoneNumber: React.FC<PhoneNumberProp> = ({
  id,
  contact,
  contactType,
  requireSms,
  ...fieldProps
}) => {
  const { register, errors } = useFormContext();

  const name = `number${id}`;

  const {
    smsNumber,
    number1Label,
    number2Label,
    number3Label,
    number1Regex,
    number2Regex,
    number3Regex,
  } = contactType || {};

  const fieldMap: Record<number, { label: any; regex: any }> = useMemo(
    () => ({
      1: {
        label: number1Label,
        regex: number1Regex?.replace(/\\/g, ""),
      },
      2: {
        label: number2Label,
        regex: number2Regex?.replace(/\\/g, ""),
      },
      3: {
        label: number3Label,
        regex: number3Regex?.replace(/\\/g, ""),
      },
    }),
    [
      number1Label,
      number1Regex,
      number2Label,
      number2Regex,
      number3Label,
      number3Regex,
    ],
  );

  const isRequired = requireSms && smsNumber === `Number${id}`;
  const { label, regex } = fieldMap[id as any];

  const fieldRule = useMemo(
    () => REGEXES.get(regex) ?? REGEXES.get("fallback")!,
    [regex],
  );

  const validateFn = useCallback(
    (value: string) => {
      if (value.length) {
        // Check if we have an exact match for the db regex locally
        if (!fieldRule.regex.test(value)) {
          return fieldRule.errorMessage;
        }
      }
      // Consider valid/without message if there is nothing in the field
      return true;
    },
    [fieldRule],
  );

  const { helperText } = fieldRule;
  return (
    <TextField
      name={name}
      label={label}
      required={isRequired}
      defaultValue={contact[name as keyof AccountContact] as string}
      error={!!errors[name]}
      helperText={errors[name] ? errors[name]?.message : helperText}
      inputProps={{
        "data-testid": `ContactDetails__TextField--Number${id}`,
      }}
      {...fieldProps}
      inputRef={register({
        required: isRequired ? "This field is required" : undefined,
        validate: validateFn,
      })}
    />
  );
};
